<template lang="pug">
//- Per-asset AI disclosure control (Art. 50(4) KI-VO). The vision assessment pre-fills
//- the toggle and explains itself; a human confirms or overrides, because the legal
//- responsibility stays with a natural person.
//-
//- Deliberately mirrors AltTextField's structure and class vocabulary: the two sit
//- directly above one another in the editor and must read as siblings, not as two
//- differently-invented panels.
.ai-disclosure-feature
    .ai-disclosure-feature__header
        .ai-disclosure-feature__label
            i.el-icon-fa-shield-halved.fas
            span {{ $t('AI labelling') }}
        .ai-disclosure-feature__meta
            //- No "KI" chip here. It meant "this file is AI", while the switch below
            //- means "show a visible label" - the same word for two different things in
            //- one box, which read as a bug. The provenance is stated in words by the
            //- "Kind of AI involvement" field instead.
            el-popover(placement="top" width="280" trigger="hover")
                span {{ $t('disclosure_hint') }}
                el-button.ai-disclosure-feature__help(slot="reference" circle size="mini") ?

    el-switch(
        :value="disclose"
        :active-text="$t('Mark as AI-generated')"
        @input="$emit('update:disclose', $event)"
    )

    p.ai-disclosure-feature__hint(v-if="rationale") {{ rationale }}
    //- Provenance on record but no visible label is a legitimate combination - a
    //- background swap is AI-made without being a deep fake - but it must never look
    //- like an oversight. When an assessment exists its rationale above already says
    //- why; this covers the case where there is none to read.
    p.ai-disclosure-feature__hint(v-else-if="sourceType && !disclose") {{ $t('recorded_without_label') }}

    el-alert.ai-disclosure-feature__warning(
        v-if="fidelityWarning"
        type="warning"
        :closable="false"
        show-icon
        :title="$t('fidelity_warning_title')"
        :description="$t('fidelity_warning_text')"
    )

    .ai-disclosure-feature__fields(v-if="disclose || sourceType")
        //- What KIND of AI involvement. "Generated" and "edited" read very differently
        //- to a shopper, and for media we did not generate ourselves the editor is the
        //- only one who knows which it was.
        //-
        //- Shown whenever the file HAS provenance, not only while the label is on: how
        //- the pixels were made is a recorded fact (the IPTC term we write into the file
        //- under Art. 50(2)), whereas the switch below is a decision about showing a
        //- visible label (Art. 50(4)). Hiding the fact behind the decision made an
        //- AI-generated file look untouched. It is read-only in that state, because with
        //- the disclosure off the backend keeps the recorded term rather than a new claim.
        .ai-disclosure-feature__field
            label.ai-disclosure-feature__field-label {{ $t('Kind of AI involvement') }}
            el-select.ai-disclosure-feature__select(
                :value="resolvedSourceType"
                size="small"
                :disabled="!disclose"
                @change="$emit('update:sourceType', $event)"
            )
                el-option(
                    v-for="kind in sourceTypes"
                    :key="kind.value"
                    :value="kind.value"
                    :label="kind.label"
                )

        .ai-disclosure-feature__field(v-if="disclose")
            label.ai-disclosure-feature__field-label {{ $t('Display on the website') }}
            el-select.ai-disclosure-feature__select(
                :value="labelDisplay"
                size="small"
                :placeholder="inheritLabel"
                clearable
                @change="$emit('update:labelDisplay', $event || null)"
            )
                el-option(:value="null" :label="inheritLabel")
                el-option(
                    v-for="mode in displayModes"
                    :key="mode.value"
                    :value="mode.value"
                    :label="mode.label"
                )

    p.ai-disclosure-feature__hint(v-if="disclose && effectiveMode === 'none'") {{ $t('display_none_hint') }}
</template>

<i18n>
{
    "de": {
        "AI labelling": "KI-Kennzeichnung",
        "KI": "KI",
        "Mark as AI-generated": "Als KI-generiert kennzeichnen",
        "Display on the website": "Anzeige auf der Website",
        "Kind of AI involvement": "Art der KI-Nutzung",
        "Fully AI-generated": "Vollständig KI-generiert",
        "Photo edited with AI": "Foto mit KI bearbeitet",
        "disclosure_hint": "Nach Art. 50 Abs. 4 KI-VO müssen KI-erzeugte oder KI-veränderte Darstellungen, die für echt gehalten werden könnten, erkennbar gekennzeichnet werden. Reine Hintergrund- oder Freistellungsbearbeitung fällt in der Regel nicht darunter.",
        "fidelity_warning_title": "Produktdarstellung prüfen",
        "fidelity_warning_text": "Die Bearbeitung hat das Produkt selbst verändert. Eine Kennzeichnung als KI-Inhalt macht eine irreführende Produktdarstellung nicht zulässig.",
        "Inherit from shop setting": "Einstellung des Shops übernehmen",
        "inherit_with_mode": "Shop-Einstellung übernehmen ({mode})",
        "Always visible": "Immer sichtbar",
        "Icon visible, details on hover": "Symbol sichtbar, Details bei Mouseover",
        "No visible marker": "Keine sichtbare Kennzeichnung",
        "display_none_hint": "Ohne sichtbare Kennzeichnung sind Sie dafür verantwortlich, die Offenlegungspflicht anderweitig zu erfüllen.",
        "recorded_without_label": "Die KI-Herkunft dieser Datei ist erfasst und in den Metadaten hinterlegt. Eine sichtbare Kennzeichnung im Shop ist nicht gesetzt."
    },
    "en": {
        "KI": "AI",
        "inherit_with_mode": "Inherit from shop setting ({mode})",
        "disclosure_hint": "Article 50(4) of the EU AI Act requires AI-generated or AI-manipulated depictions that could be taken as authentic to be clearly labelled. Background replacement and cut-outs normally fall outside this.",
        "fidelity_warning_title": "Check the product depiction",
        "fidelity_warning_text": "The edit changed the product itself. Labelling content as AI does not make a misleading product depiction permissible.",
        "display_none_hint": "Without a visible marker you are responsible for meeting the disclosure duty another way.",
        "recorded_without_label": "This file's AI provenance is on record and written into its metadata. No visible label is shown in the shop."
    }
}
</i18n>

<script>
import _ from 'lodash';

export default {
    name: 'AiDisclosureField',
    props: {
        // Whether this asset needs a perceivable disclosure (.sync).
        disclose: { type: Boolean, default: false },
        // Per-asset override of the shop-wide display mode; null inherits it (.sync).
        labelDisplay: { type: String, default: null },
        // IPTC DigitalSourceType term: fully generated vs a photo with AI applied to
        // it. Written by the pipeline for our own generations, declared by the editor
        // for anything uploaded (.sync).
        sourceType: { type: String, default: null },
        // The stored assessment record, used only to explain the pre-filled decision.
        assessment: { type: Object, default: null },
        // The shop-wide default, shown so "inherit" is not a blind choice.
        shopLabelDisplay: { type: String, default: 'permanent' },
    },
    computed: {
        displayModes() {
            return [
                { value: 'permanent', label: this.$t('Always visible') },
                { value: 'hover', label: this.$t('Icon visible, details on hover') },
                { value: 'none', label: this.$t('No visible marker') },
            ];
        },
        sourceTypes() {
            return [
                { value: 'trainedAlgorithmicMedia', label: this.$t('Fully AI-generated') },
                { value: 'compositeWithTrainedAlgorithmicMedia', label: this.$t('Photo edited with AI') },
            ];
        },
        // An asset the editor is declaring has no provenance yet. Show the value the
        // backend would fall back to, so the select never sits empty on something the
        // shopper will see labelled.
        resolvedSourceType() {
            return this.sourceType || 'trainedAlgorithmicMedia';
        },
        fidelityWarning() {
            return _.get(this.assessment, 'fidelity_warning') === true;
        },
        // Why the switch is in the state it is. Without this the editor is asked to
        // confirm a decision with no visible basis for it.
        //
        // Only an ASSESSMENT has reasoning worth showing. A declaration was made by the
        // editor themselves, so restating it under the switch they just flipped explains
        // nothing - and older records stored that sentence in English regardless of the
        // interface language, because it is data rather than a translatable string.
        rationale() {
            if (_.get(this.assessment, 'origin') === 'declared') return '';

            return _.get(this.assessment, 'rationale') || '';
        },
        // What actually applies once the override falls back to the shop default.
        effectiveMode() {
            return this.labelDisplay || this.shopLabelDisplay || 'permanent';
        },
        inheritLabel() {
            const current = _.find(this.displayModes, { value: this.shopLabelDisplay });
            if (!current) {
                return this.$t('Inherit from shop setting');
            }
            return this.$t('inherit_with_mode', { mode: current.label });
        },
    },
};
</script>

<style lang="sass" scoped>
.ai-disclosure-feature
    border: 1px solid #ddd6fe
    border-radius: 10px
    background: linear-gradient(140deg, #faf8ff 0%, #eff0ff 100%)
    padding: 12px 14px
    width: 100%
    // Element sets a control-sized line-height (36px) on .el-form-item__content, so a
    // panel dropped into a form inherits it and every text row inside is stretched to
    // the height of an input. Own the rhythm instead of taking the host's.
    line-height: 1.4

    &__header
        display: flex
        align-items: center
        justify-content: space-between
        margin-bottom: 10px

    &__label
        display: flex
        align-items: center
        gap: 6px
        font-size: 12px
        font-weight: 600
        color: #5b4de8
        i
            font-size: 11px
            margin-right: 4px

    &__meta
        display: flex
        align-items: center
        gap: 6px

    &__badge
        font-size: 10px
        font-weight: 700
        letter-spacing: 0.5px
        background: #5b4de8 !important
        border-color: #5b4de8 !important
        color: #fff !important
        border-radius: 4px
        padding: 0 5px

    // Same dimensions as AltTextField's help button: the two sit in the same column
    // and any difference reads as a misalignment.
    // globals.sass sets `.el-button.is-circle.el-button--mini { min-width: 28px }`,
    // which outranks a single scoped class and stretched this past the sibling
    // panel's help button. Match that specificity rather than reaching for
    // !important.
    // Copied verbatim from AltTextField's __help: the two buttons sit in the same
    // column and any difference in size, tint or weight reads as a misalignment.
    // `min-width: auto !important` is required because globals.sass sets
    // `body .el-button.is-circle.el-button--mini { min-width: 28px }`.
    &__help
        padding: 0
        height: 20px
        width: 20px
        min-height: auto
        min-width: auto !important
        font-size: 11px
        font-weight: 600
        background: #ede9fe
        border-color: #c4b5fd
        color: #5b4de8

    // Side by side, each keeping its label stacked above the control: the option
    // names are long enough that an INLINE label squeezed the select until its own
    // value was clipped. Wraps to one column when the panel gets narrow.
    &__fields
        display: flex
        flex-wrap: wrap
        gap: 12px
        margin-top: 12px

    &__field
        flex: 1 1 220px
        min-width: 0

    &__field-label
        display: block
        margin-bottom: 4px
        font-size: 12px
        font-weight: 600
        color: #5b4de8

    &__select
        width: 100%

    &__hint
        margin: 6px 0 0
        font-size: 11px
        color: #9ca3af
        line-height: 1.4

    &__warning
        margin-top: 10px
</style>
